
import React, { useEffect, useMemo, useState } from "react";
import { Navigation } from "../components/navigation";
import { Footer } from "../components/footer";
import "./newsfeed/newsfeed.css";

const normalizeBaseUrl = (value) => (value ? value.replace(/\/$/, "") : "");

const formatDate = (value) => {
  if (!value) return "";
  try {
    const parsed = typeof value === "string" ? new Date(value) : value;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(parsed);
  } catch (err) {
    return "";
  }
};

export const NewsfeedPage = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const apiBaseUrl = useMemo(() => normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL), []);
  const newsEndpoint = useMemo(() => {
    if (apiBaseUrl) return `${apiBaseUrl}/news`;
    return "/news";
  }, [apiBaseUrl]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchNews = async () => {
      try {
        const response = await fetch(newsEndpoint, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to load news (${response.status})`);
        }
        const payload = await response.json();
        setNewsItems(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        setError(err.message || "Unable to load news");
      } finally {
        setLoading(false);
      }
    };

    fetchNews();

    return () => controller.abort();
  }, [newsEndpoint]);

  return (
    <>
      <Navigation />
      <main className="newsfeed-page">
        <div className="newsfeed-page__header">
          <h3 className="newsfeed-page__title">ကျောင်းတိုက်သတင်းများ</h3>
        </div>
        <div className="newsfeed-feed">
          {loading && <p className="newsfeed-status">Loading news…</p>}
          {error && !loading && <p className="newsfeed-status">{error}</p>}
          {!loading && !error && newsItems.length === 0 && (
            <p className="newsfeed-status">No news items available yet.</p>
          )}
          {!loading &&
            newsItems.map((newsItem, index) => {
              const id = newsItem.id ?? index;
              const imageSrc =
                newsItem.imageUrl?.trim() ||
                newsItem.image?.trim() ||
                null;

              return (
                <article key={`news-${id}`} className="newsfeed-post">
                  <div className="newsfeed-post__header">
                    <img
                      className="newsfeed-post__avatar"
                      src="img/favicon.png"
                      alt="Community avatar"
                    />
                    <div className="newsfeed-post__user">
                      <div className="newsfeed-post__name-row">
                        <span className="newsfeed-post__author">Admin</span>
                        <span className="newsfeed-post__time">
                          {formatDate(newsItem.date)}
                        </span>
                      </div>
                      <span className="newsfeed-post__meta">
                        {newsItem.remark || "Community update"}
                      </span>
                    </div>
                  </div>
                  <div className="newsfeed-post__content">
                    <h4 className="newsfeed-post__title">{newsItem.title}</h4>
                    <p className="newsfeed-post__body">{newsItem.content}</p>
                    {imageSrc && (
                      <div className="newsfeed-post__media">
                        <div
                          className="newsfeed-post__image-wrapper"
                          role="button"
                          tabIndex={0}
                          aria-label={`Open preview for ${newsItem.title}`}
                          onClick={() => {
                            setPreviewImage(imageSrc);
                            setPreviewTitle(newsItem.title || "");
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setPreviewImage(imageSrc);
                              setPreviewTitle(newsItem.title || "");
                            }
                          }}
                        >
                          <img
                            src={imageSrc}
                            alt={newsItem.title || "news image"}
                            className="newsfeed-post__image"
                          />
                          <div className="newsfeed-post__image-overlay">
                            <span className="newsfeed-post__overlay-label">
                              View image
                            </span>
                            <p className="newsfeed-post__overlay-detail">
                              {newsItem.remark || "Open in a larger preview"}
                            </p>
                            <span className="newsfeed-post__overlay-link">
                              Tap to preview
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
    </div>
  </main>
  {previewImage && (
    <div className="newsfeed-preview">
      <div
        className="newsfeed-preview__backdrop"
        onClick={() => setPreviewImage(null)}
      />
      <div className="newsfeed-preview__content">
        <button
          type="button"
          className="newsfeed-preview__close"
          onClick={() => setPreviewImage(null)}
          aria-label="Close preview"
        >
          ×
        </button>
        <img src={previewImage} alt={previewTitle || "news image"} />
        {previewTitle && (
          <p className="newsfeed-preview__title">{previewTitle}</p>
        )}
      </div>
    </div>
  )}
  <Footer />
</>
);
};

export default NewsfeedPage;
